
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Thread } from "./types";

export const useThreads = (clubId: string) => {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchThreads = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from("club_threads")
        .select(`
          *,
          profiles:user_id (id, full_name, username, avatar_url),
          replies:club_thread_replies (count),
          likes:thread_likes (count)
        `)
        .eq("club_id", clubId)
        .order("created_at", { ascending: false });
        
      if (selectedCategory) {
        query = query.eq("category", selectedCategory);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      if (data) {
        // Check if user has liked any of these threads
        let threadsWithLikes = [...data];
        
        if (user) {
          const { data: userLikes } = await supabase
            .from("thread_likes")
            .select("thread_id")
            .eq("user_id", user.id);
          
          const likedThreadIds = userLikes?.map(like => like.thread_id) || [];
          
          threadsWithLikes = data.map(thread => ({
            ...thread,
            liked: likedThreadIds.includes(thread.id),
            reply_count: thread.replies.length,
            likes_count: thread.likes.length,
            author: thread.profiles
          }));
        }
        
        setThreads(threadsWithLikes);
      }
    } catch (error) {
      console.error("Error fetching club threads:", error);
      toast({
        title: "Error",
        description: "Failed to load discussion threads",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLikeThread = async (threadId: string, isCurrentlyLiked: boolean) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to like threads",
        variant: "destructive",
      });
      return;
    }
    
    try {
      // Optimistically update the UI
      setThreads(threads.map(thread => {
        if (thread.id === threadId) {
          return {
            ...thread,
            liked: !isCurrentlyLiked,
            likes_count: isCurrentlyLiked 
              ? thread.likes_count - 1 
              : thread.likes_count + 1
          };
        }
        return thread;
      }));
      
      if (isCurrentlyLiked) {
        // Remove the like
        await supabase
          .from("thread_likes")
          .delete()
          .eq("thread_id", threadId)
          .eq("user_id", user.id);
      } else {
        // Add the like
        await supabase
          .from("thread_likes")
          .insert({
            thread_id: threadId,
            user_id: user.id
          });
      }
      
    } catch (error) {
      console.error("Error updating like:", error);
      toast({
        title: "Error",
        description: "Failed to update like",
        variant: "destructive",
      });
      // Revert the optimistic update
      fetchThreads();
    }
  };

  useEffect(() => {
    fetchThreads();
  }, [clubId, selectedCategory]);

  return {
    threads,
    loading,
    selectedCategory,
    setSelectedCategory,
    fetchThreads,
    handleLikeThread
  };
};
